import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import AssetsLoadManager from '@Core/Loader/AssetsLoadManager';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { EBullshitCollectorSimulationType, IBullshitCollector, IBullshitCollectorConfig } from '@GFTypes/GameStoreTypes';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import randomValue from '@Tools/RandomValue';
import { GameScene } from '..';

export class BullshitCollector extends GameComponent {
    static readonly SPINE_KEY: string = 'collectors';
    static readonly SKIN_NAME: string = 'default';
    public scene: GameScene;
    private spineWrapper: SpineWrapper | undefined;
    private hasDedicatedSpine: boolean;
    private configs: IBullshitCollectorConfig[];
    constructor(scene: GameScene) {
        super(scene);
        this.scene = scene;
        Logger.log(LoggerLevel.Constructor, {
            source: LoggerSource.Game,
            message: 'BullshitCollector constructor'
        });

        EventsCenter.on(EGameEvents.ACTION_RESPONSE_PROCESSED, this.onResponse, this);
    }

    private initSpine(): void {
        this.hasDedicatedSpine = AssetsLoadManager.getSpinesConfiguration().assets.some((asset) => asset.key === 'collectors');
        if (this.hasDedicatedSpine) {
            this.spineWrapper = new SpineWrapper(
                this.scene,
                {
                    x: this.scene.screen.width / 2,
                    y: this.scene.screen.height / 2
                },
                { dataKey: BullshitCollector.SPINE_KEY, skinName: BullshitCollector.SKIN_NAME },
                true
            );
            this.spineWrapper.spine.zIndex = ELayeringOrder.REELS_BACKGROUND;
        } else {
            this.spineWrapper = this.scene.game.spines.find((spine) =>
                spine.eventManager.hasGameEventAnimation(EGameEvents.COLLECTOR_$_LEVEL_$_IN, true, [1, 1])
            );
        }
    }

    private async initCollectors() {
        const collectors: IBullshitCollector[] = [];

        this.configs.forEach((config, index) => {
            collectors.push({
                id: config.id,
                current: 1,
                total: config.simulation.total,
                currentLevel: 1,
                currentLevelDeviation: randomValue(-config.simulation.countDeviation, config.simulation.countDeviation)
            });

            //TODO reset based on event string
            //config.simulation.resetOnEvents.forEach((eventName) => {});
        });

        this.gameStore.set(EGameStoreKeys.Collectors, { activeBullshitCollectors: collectors });

        this.setLevel();
    }

    public onCreate(): void {
        const collectorConfigs = ConfigLoader.getConfiguration(EConfigurationNames.GAME).collectors;
        if (!collectorConfigs) return;

        this.configs = collectorConfigs;

        this.initSpine();
        this.initCollectors();

        EventsCenter.on(EGameStates.SPECIAL_GAMES_SUMMARY_STATE, this.resetLevel, this);
    }

    private onResponse(): void {
        const collectors = this.gameStore.get(EGameStoreKeys.Collectors);
        if (!collectors) return;

        const activeBullshitCollectors = [...collectors.activeBullshitCollectors.map((collector) => ({ ...collector }))];
        const bet = this.scene.game.betManager.getCurrentSpinBet();

        this.configs?.forEach((config, index) => {
            const collector = activeBullshitCollectors[index];
            if (config.simulation.basedOn === EBullshitCollectorSimulationType.SPINS && bet) {
                collector.current++;
                collector.current = Math.min(collector.current, collector.total);

                const level = this.getCurrentLevel(config, collector);

                if (collector.currentLevel !== level) {
                    collector.currentLevel = level;
                    collector.currentLevelDeviation = randomValue(-config.simulation.countDeviation, config.simulation.countDeviation);
                    this.changeLevel(collector);
                }
            }
        });

        this.gameStore.set(EGameStoreKeys.Collectors, { activeBullshitCollectors }, true);
    }

    private async setLevel(): Promise<void> {
        const collectors = this.gameStore.get(EGameStoreKeys.Collectors);
        if (!collectors) return;

        for (const collector of collectors.activeBullshitCollectors) {
            await this.changeLevel(collector);
        }
    }

    private async changeLevel(collector: IBullshitCollector): Promise<void> {
        EventsCenter.emit(EGameEvents.COLLECTOR_$_LEVEL_$_IN, { eventIds: [collector.id!, collector.currentLevel] });

        await this.spineWrapper?.waitForAnimationEvent(EGameEvents.COLLECTOR_$_LEVEL_$_IN, 'complete', [collector.id!, collector.currentLevel]).promise;

        EventsCenter.emit(EGameEvents.COLLECTOR_$_LEVEL_$, { eventIds: [collector.id!, collector.currentLevel] });
    }

    private getCurrentLevel(config: IBullshitCollectorConfig, collector: IBullshitCollector): number {
        const deviation = collector.currentLevelDeviation;
        const level = Math.max(
            collector.currentLevel - 1,
            config.simulation.countPerLevel.findIndex((level) => collector.current + deviation <= level)
        );
        return level + 1;
    }

    private resetLevel(): void {
        const collectors = this.gameStore.get(EGameStoreKeys.Collectors);
        if (!collectors) return;
        const activeBullshitCollectors = [...collectors.activeBullshitCollectors.map((collector) => ({ ...collector }))];
        activeBullshitCollectors.forEach((collector, index) => {
            collector.current = 1;
            collector.currentLevel = 1;
            collector.currentLevelDeviation = randomValue(-this.configs[index].simulation.countDeviation, this.configs[index].simulation.countDeviation);
            this.changeLevel(collector);
        });
        this.gameStore.set(EGameStoreKeys.Collectors, { activeBullshitCollectors }, true);
    }
}
